// =====================================================================
// AgendaMedica — iframe da agenda do paciente, multi-tenant.
//
// Detecta tenant slug:
//   1) localStorage `agendaMedica_selectedSlug` (escolha do super-admin)
//   2) Primeira clinic acessivel via /clinic-admin/clinics (admin local
//      so ve 1; super-admin ve todas e pode trocar pelo dropdown)
//   3) Fallback 'clee' (clinica raiz)
//
// Multi-tenant white-label S38.
// =====================================================================
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Box
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import { listAccessibleClinics } from "../../services/clinicAdminService";

const AGENDA_HOST = "https://agenda.genius.med.br";
const STORAGE_KEY = "agendaMedica_selectedSlug";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  // Toolbar superior compacta — só aparece se super-admin (>1 clinic)
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    minHeight: 48,
  },
  toolbarLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
  tenantSelect: {
    flexGrow: 1,
    minWidth: 130,
  },
  iframeWrapper: {
    flex: 1,
    width: "100%",
    height: "calc(100vh - 120px)",
    position: "relative",
    background: theme.palette.background.default,
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "calc(100vh - 200px)",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  // Fase 5 v1.6 — botao DISCRETO so com icone, opacity baixa, full no hover
  floatingOpenBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 10,
    width: 26,
    height: 26,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    fontSize: "0.875rem",
    fontWeight: 700,
    textDecoration: "none",
    lineHeight: 1,
    color: theme.palette.type === "dark" ? "#fff" : theme.palette.primary.main,
    background:
      theme.palette.type === "dark"
        ? "rgba(20,20,20,0.4)"
        : "rgba(255,255,255,0.5)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(10,37,64,0.08)"
    }`,
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
    userSelect: "none",
    cursor: "pointer",
    opacity: 0.35,
    "&:hover": {
      opacity: 1,
      background:
        theme.palette.type === "dark"
          ? "rgba(37,213,232,0.22)"
          : "rgba(37,213,232,0.15)",
      borderColor: theme.palette.primary.main,
      boxShadow: "0 4px 12px rgba(0,0,0,0.22)",
      transform: "scale(1.1)",
    },
  },
}));

const AgendaMedica = () => {
  const classes = useStyles();
  const history = useHistory();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);

  // Carrega clinics acessiveis no mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const rows = await listAccessibleClinics();
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setClinics(list);

        // Determina slug ativo:
        // 1) localStorage (se ainda eh valido — esta na lista)
        // 2) Primeiro clinic acessivel
        // 3) Fallback 'clee'
        let slug = null;
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored && list.some((c) => c.slug === stored)) {
            slug = stored;
          }
        } catch (_e) { /* localStorage pode falhar em incognito */ }
        if (!slug) {
          slug = list.length > 0 ? list[0].slug : "clee";
        }
        setSelectedSlug(slug);
      } catch (err) {
        // Sem clinics ou erro de auth — fallback pra slug 'clee' (legado)
        if (!cancelled) {
          setClinics([]);
          setSelectedSlug("clee");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // [S70] escuta o botão "Abrir ficha" da agenda (iframe) e abre o prontuário do paciente.
  // Valida a origem (só a agenda white-label) — LGPD/segurança.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== AGENDA_HOST) return;
      const d = event.data;
      if (!d || d.type !== "genius:open-patient") return;
      const q = String(d.patient_name || d.patient_phone || "").trim();
      if (q) history.push(`/prontuario?busca=${encodeURIComponent(q)}`);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [history]);

  const handleChangeSlug = (event) => {
    const newSlug = event.target.value;
    setSelectedSlug(newSlug);
    try {
      localStorage.setItem(STORAGE_KEY, newSlug);
    } catch (_e) { /* swallow */ }
  };

  const agendaUrl = useMemo(() => {
    if (!selectedSlug) return null;
    return `${AGENDA_HOST}/${selectedSlug}`;
  }, [selectedSlug]);

  const currentClinic = useMemo(
    () => clinics.find((c) => c.slug === selectedSlug),
    [clinics, selectedSlug]
  );

  // Dropdown só aparece se super-admin (>1 clinic acessivel)
  const showSwitcher = clinics.length > 1;

  if (loading) {
    return (
      <MainContainer>
        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.loadingWrap}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Carregando agenda…
            </Typography>
          </div>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} variant="outlined">
        {showSwitcher && (
          <Box className={classes.toolbar}>
            <Typography className={classes.toolbarLabel}>Clínica:</Typography>
            <FormControl
              size="small"
              variant="outlined"
              className={classes.tenantSelect}
            >
              <Select
                value={selectedSlug || ""}
                onChange={handleChangeSlug}
                displayEmpty
              >
                {clinics.map((c) => (
                  <MenuItem key={c.slug} value={c.slug}>
                    {c.clinic_name || c.slug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentClinic && (
              <Typography variant="caption" color="textSecondary">
                {agendaUrl}
              </Typography>
            )}
          </Box>
        )}
        <div className={classes.iframeWrapper}>
          {agendaUrl && (
            <>
              <iframe
                key={agendaUrl}
                src={agendaUrl}
                className={classes.iframe}
                title={`Agenda Médica — ${currentClinic?.clinic_name || selectedSlug}`}
                allow="clipboard-read; clipboard-write"
              />
              <a
                href={agendaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.floatingOpenBtn}
                title="Abrir agenda em nova aba (modo tela cheia)"
              >
                ↗
              </a>
            </>
          )}
        </div>
      </Paper>
    </MainContainer>
  );
};

export default AgendaMedica;
